import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Page } from '../../components/shell/Page';
import ExplorerLayout from '../../components/shell/ExplorerTree';
import SplitLayout from '../../components/shell/SidePane';
import { useCategoryManagerPane } from '../../components/categories/CategoryManager';
import { useCourseExplorer } from '../../components/courses/CourseExplorer';
import DataGrid, { StatusBadge } from '../../components/datagrid/DataGrid';
import { Button } from '../../components/ui/button';
import { Plus, Trash, Pencil, Eye, TreeStructure, PlayCircle, FolderOpen } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { openLearnerPreview } from '../../components/PreviewMode';

export default function InstructorCourses() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryFilter, setCategoryFilter] = useState(null); // from the explorer
  const explorer = useCourseExplorer({ api, onCategory: (k) => setCategoryFilter(k), storageKey: 'courses' });
  const categoryMgr = useCategoryManagerPane({ api, onChange: () => { fetchCourses(); explorer.reload(); } });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const res = await api.get('/courses');
      setCourses(res.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteCourse = async (courseId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!window.confirm('Are you sure you want to delete this course?')) return;

    try {
      await api.delete(`/courses/${courseId}`);
      toast.success('Course deleted');
      fetchCourses();
      explorer.reload();
    } catch (error) {
      toast.error('Failed to delete course');
    }
  };

  const openCourse = (course) => navigate(`/instructor/courses/${course.id}`);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-8 flex items-center justify-center min-h-[60vh]">
          <div className="animate-pulse">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <ExplorerLayout explorer={explorer.props}>
      <SplitLayout side={categoryMgr.side}>
      <Page data-testid="instructor-courses-page">
        <DataGrid
          title="My Courses"
          headerExtra={categoryFilter && (
            <button type="button" onClick={() => { setCategoryFilter(null); explorer.setSelectedKey(null); }} className="inline-flex items-center gap-1 h-8 px-2.5 rounded border border-shell-primary/40 bg-shell-primary/5 text-xs text-shell-primary hover:bg-shell-primary/10" title="Clear category filter">
              Category: <span className="font-medium">{(explorer.catalog.byKey(`cat:${categoryFilter}`)?.node.label) || categoryFilter}</span> ×
            </button>
          )}
          rows={categoryFilter ? courses.filter((c) => (c.category_id || (c.category || 'uncategorised').toLowerCase()) === categoryFilter) : courses}
          loading={loading}
          onRefresh={fetchCourses}
          searchKeys={['description']}
          columns={[
            { key: 'title', header: 'Title', render: (c) => <span className="font-medium">{c.title}</span> },
            { key: 'course_code', header: 'Code', width: 120, accessor: (c) => c.course_code || '', className: 'font-mono text-xs text-muted-foreground' },
            { key: 'category', header: 'Category', width: 150, accessor: (c) => c.category_name || c.category || '', render: (c) => c.category_name ? <span title={c.category_path || c.category_name}>{c.category_path && c.category_path !== c.category_name ? <><span className="text-muted-foreground">{c.category_path.slice(0, -c.category_name.length)}</span>{c.category_name}</> : c.category_name}</span> : c.category ? <span className="capitalize text-muted-foreground" title="Legacy category — edit the course to file it in the shared list">{c.category}</span> : '—' },
            { key: 'enrollment_count', header: 'Enrollments', width: 120, align: 'right', accessor: (c) => c.enrollment_count || 0, className: 'tabular-nums' },
            { key: 'is_published', header: 'Published', width: 120, accessor: (c) => !!c.is_published, render: (c) => <StatusBadge value={c.is_published ? 'published' : 'draft'} /> },
            { key: 'enable_certificate', header: 'Certificate', width: 120, accessor: (c) => !!c.enable_certificate, render: (c) => <StatusBadge value={c.enable_certificate ? 'Enabled' : 'Disabled'} tone={c.enable_certificate ? 'success' : 'muted'} /> },
            { key: 'updated_at', header: 'Updated', width: 120, render: (c) => (c.updated_at ? new Date(c.updated_at).toLocaleDateString() : '—') },
          ]}
          rowTone={(c) => (c.is_published ? 'success' : null)}
          onRowDoubleClick={openCourse}
          primaryActions={(
            <>
            <Button size="sm" className="h-9" onClick={() => navigate('/instructor/courses/new')} data-testid="create-course-btn">
              <Plus className="w-4 h-4" /> New
            </Button>
              <Button size="sm" variant="outline" className="h-9" onClick={categoryMgr.open} data-testid="manage-categories-btn">
                <FolderOpen className="w-4 h-4" /> Manage categories
              </Button>
            </>
          )}
          selectionActions={[
            { key: 'view', label: 'View', icon: Eye, onClick: ([c]) => openCourse(c) },
            { key: 'preview', label: 'Preview', icon: PlayCircle, onClick: ([c]) => openLearnerPreview(`/course/${c.id}`), testid: 'preview-course' },
            { key: 'edit', label: 'Edit', icon: Pencil, onClick: ([c]) => navigate(`/instructor/courses/${c.id}/edit`) },
            { key: 'structure', label: 'Structure', icon: TreeStructure, onClick: ([c]) => navigate(`/instructor/courses/${c.id}/structure`) },
            { key: 'delete', label: 'Delete', icon: Trash, variant: 'destructive', onClick: ([c]) => handleDeleteCourse(c.id) },
          ]}
          emptyMessage="No courses yet. Create your first course to get started."
        />

      </Page>
      </SplitLayout>
      </ExplorerLayout>
    </DashboardLayout>
  );
}
